/**
 * Decals - marcas de bala projetadas na superficie atingida.
 *
 * Decisoes que valem a pena explicar:
 *
 * 1. O decalque usa MeshStandardMaterial (com injecao por onBeforeCompile), e nao
 *    um shader unlit proprio. Custa um pouco mais, mas o furo passa a ser iluminado
 *    pelo MESMO sol, pelo MESMO environment e a RECEBER a mesma sombra da parede
 *    em que esta colado. Decalque unlit e o erro classico: vira adesivo brilhando
 *    dentro da sombra e denuncia o jogo na hora.
 *
 * 2. Um unico InstancedMesh com atlas 2x2 de furos + normal map procedural. O
 *    normal map e o que da a cratera: sem ele o furo e um desenho plano.
 *
 * 3. Fila circular com DOIS fades combinados: por idade e por "pressao da fila".
 *    Os decalques que estao prestes a ser reciclados desaparecem suavemente ANTES
 *    de serem sobrescritos, entao nunca ha pop de decalque sumindo do nada.
 *
 * 4. Vidro tem tile proprio (teia de rachaduras), nao furo com po.
 */

import * as THREE from 'three';
import { LAYERS } from '../core/Constants.js';

const CAP = 256;                 // teto do preset ultra; a fila viva usa settings.maxDecals
const FADE_SLOTS = 7;            // quantos decalques a frente do cursor ja vao sumindo
const LIFE = 26;                 // segundos ate comecar a sumir por idade
const NORMAL_OFFSET = 0.008;     // deslocamento ao longo da normal (m)

/**
 * Estilo por superficie: qual tile do atlas, tamanho, cor e acabamento.
 * tile 0 = furo em material duro, 1 = cratera em material macio,
 * tile 2 = furo em metal, 3 = teia em vidro.
 */
const STYLES = {
  concrete: { tile: 0, s0: 0.13, s1: 0.19, r: 0.82, g: 0.80, b: 0.77, rough: 0.08, metal: 0.0 },
  asphalt: { tile: 0, s0: 0.12, s1: 0.17, r: 0.46, g: 0.45, b: 0.44, rough: 0.10, metal: 0.0 },
  brick: { tile: 0, s0: 0.12, s1: 0.18, r: 0.80, g: 0.58, b: 0.47, rough: 0.10, metal: 0.0 },
  plaster: { tile: 0, s0: 0.15, s1: 0.22, r: 0.92, g: 0.90, b: 0.86, rough: 0.06, metal: 0.0 },
  tile: { tile: 0, s0: 0.10, s1: 0.15, r: 0.86, g: 0.85, b: 0.83, rough: -0.10, metal: 0.0 },
  stone: { tile: 0, s0: 0.13, s1: 0.19, r: 0.78, g: 0.77, b: 0.74, rough: 0.08, metal: 0.0 },
  wood: { tile: 0, s0: 0.09, s1: 0.14, r: 0.42, g: 0.28, b: 0.16, rough: 0.10, metal: 0.0 },
  metal: { tile: 2, s0: 0.07, s1: 0.11, r: 0.86, g: 0.85, b: 0.84, rough: -0.48, metal: 0.85 },
  metal_thin: { tile: 2, s0: 0.06, s1: 0.10, r: 0.88, g: 0.87, b: 0.86, rough: -0.52, metal: 0.9 },
  glass: { tile: 3, s0: 0.34, s1: 0.55, r: 0.90, g: 0.95, b: 1.00, rough: -0.55, metal: 0.0 },
  dirt: { tile: 1, s0: 0.16, s1: 0.24, r: 0.34, g: 0.27, b: 0.19, rough: 0.10, metal: 0.0 },
  sand: { tile: 1, s0: 0.17, s1: 0.26, r: 0.62, g: 0.54, b: 0.38, rough: 0.10, metal: 0.0 },
  gravel: { tile: 1, s0: 0.15, s1: 0.22, r: 0.40, g: 0.38, b: 0.35, rough: 0.10, metal: 0.0 },
  grass: { tile: 1, s0: 0.15, s1: 0.22, r: 0.26, g: 0.26, b: 0.16, rough: 0.10, metal: 0.0 },
  fabric: { tile: 1, s0: 0.07, s1: 0.11, r: 0.22, g: 0.21, b: 0.20, rough: 0.12, metal: 0.0 },
  rubber: { tile: 1, s0: 0.07, s1: 0.10, r: 0.13, g: 0.13, b: 0.13, rough: 0.06, metal: 0.0 },
  sandbag: { tile: 1, s0: 0.10, s1: 0.15, r: 0.58, g: 0.50, b: 0.35, rough: 0.12, metal: 0.0 }
};
// flesh e water nao recebem decalque.

// --- temporarios de modulo (nada de alocar por tiro) ------------------------
const _n = new THREE.Vector3();
const _t = new THREE.Vector3();
const _b = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _basis = new THREE.Matrix4();
const _mat = new THREE.Matrix4();

// --- ruido para a geracao das texturas (roda uma vez, no init) --------------
function hash2(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return s - Math.floor(s);
}
function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x, y, oct) {
  let s = 0, amp = 0.5, f = 1;
  for (let i = 0; i < oct; i++) {
    s += vnoise(x * f, y * f) * amp;
    amp *= 0.5;
    f *= 2.07;
  }
  return s;
}
function wrapAngle(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

const DECL_VERT = /* glsl */`
attribute vec4 iData;   // birth, life, tile, roughAdd
attribute vec4 iTint;   // rgb, metalness
attribute float iSlot;
uniform float uDecalTime;
uniform float uDecalHead;
uniform float uDecalLimit;
varying vec2 vDecalUv;
varying vec3 vDecalTint;
varying float vDecalFade;
varying float vDecalRough;
varying float vDecalMetal;
varying vec3 vDecalTanV;
varying vec3 vDecalBitV;
`;

const BODY_VERT = /* glsl */`
  float dAge = uDecalTime - iData.x;
  float dLife = max(iData.y, 0.001);
  float dK = dAge / dLife;
  // sem fade de entrada de proposito: furo de bala tem que aparecer INTEIRO no
  // primeiro frame depois do tiro. So a saida e suave.
  float fade = 1.0 - smoothstep(0.70, 1.0, dK);
  // pressao da fila: o decalque no cursor e o proximo a ser sobrescrito, entao
  // ele ja precisa estar invisivel quando isso acontecer
  float ahead = mod(iSlot - uDecalHead + uDecalLimit, uDecalLimit);
  fade *= smoothstep(0.0, ${FADE_SLOTS.toFixed(1)}, ahead);
  if (iSlot >= uDecalLimit) fade = 0.0;
  vDecalFade = fade;
  if (fade <= 0.002) transformed = vec3(0.0);

  float dTile = iData.z;
  vec2 dOff = vec2(mod(dTile, 2.0), floor(dTile * 0.5)) * 0.5;
  vDecalUv = dOff + (uv * 0.98 + 0.01) * 0.5;
  vDecalTint = iTint.rgb;
  vDecalMetal = iTint.a;
  vDecalRough = iData.w;

  // tangente e bitangente do quad em espaco de camera (o plano tem T=+X, B=+Y)
  mat3 dIM = mat3(instanceMatrix);
  vDecalTanV = normalize(normalMatrix * normalize(dIM * vec3(1.0, 0.0, 0.0)));
  vDecalBitV = normalize(normalMatrix * normalize(dIM * vec3(0.0, 1.0, 0.0)));
`;

const DECL_FRAG = /* glsl */`
uniform sampler2D uDecalMap;
uniform sampler2D uDecalNormal;
uniform float uDecalNormalScale;
varying vec2 vDecalUv;
varying vec3 vDecalTint;
varying float vDecalFade;
varying float vDecalRough;
varying float vDecalMetal;
varying vec3 vDecalTanV;
varying vec3 vDecalBitV;
`;

const MAP_FRAG = /* glsl */`
  vec4 dTex = texture2D(uDecalMap, vDecalUv);
  diffuseColor.rgb *= dTex.rgb * vDecalTint;
  diffuseColor.a *= dTex.a * vDecalFade;
  if (diffuseColor.a < 0.004) discard;
`;

const ROUGH_FRAG = /* glsl */`
  float roughnessFactor = clamp(roughness + vDecalRough + (1.0 - dTex.g) * 0.12, 0.04, 1.0);
`;

const METAL_FRAG = /* glsl */`
  float metalnessFactor = vDecalMetal;
`;

const NORMAL_FRAG = /* glsl */`
  vec3 dN = texture2D(uDecalNormal, vDecalUv).xyz * 2.0 - 1.0;
  dN.xy *= uDecalNormalScale;
  normal = normalize(mat3(vDecalTanV, vDecalBitV, normal) * normalize(dN));
`;

export class Decals {
  constructor(ctx) {
    this.ctx = ctx;
    this.mesh = null;
    this.material = null;
    this.mapTex = null;
    this.normalTex = null;
    this._head = 0;
    this._filled = 0;
    this._time = 0;

    this._uTime = { value: 0 };
    this._uHead = { value: 0 };
    this._uLimit = { value: CAP };
    this._uNormalScale = { value: 0.9 };
  }

  async init() {
    const built = this._buildTextures();
    this.mapTex = built.map;
    this.normalTex = built.normal;

    const geo = new THREE.PlaneGeometry(1, 1);

    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.85,
      metalness: 0.0,
      transparent: true,
      depthWrite: false,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4
    });
    // sem chave propria, o three reusaria o programa do MeshStandardMaterial comum
    mat.customProgramCacheKey = () => 'blackout-decal-v1';
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uDecalMap = { value: this.mapTex };
      shader.uniforms.uDecalNormal = { value: this.normalTex };
      shader.uniforms.uDecalNormalScale = this._uNormalScale;
      shader.uniforms.uDecalTime = this._uTime;
      shader.uniforms.uDecalHead = this._uHead;
      shader.uniforms.uDecalLimit = this._uLimit;

      shader.vertexShader = shader.vertexShader
        .replace('void main() {', DECL_VERT + '\nvoid main() {')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\n' + BODY_VERT);

      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', DECL_FRAG + '\nvoid main() {')
        .replace('#include <map_fragment>', MAP_FRAG)
        .replace('#include <roughnessmap_fragment>', ROUGH_FRAG)
        .replace('#include <metalnessmap_fragment>', METAL_FRAG)
        .replace('#include <normal_fragment_maps>', NORMAL_FRAG);
    };
    this.material = mat;

    const mesh = new THREE.InstancedMesh(geo, mat, CAP);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.renderOrder = 6;
    mesh.matrixAutoUpdate = false;
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

    const data = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
    const tint = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 4), 4);
    const slot = new THREE.InstancedBufferAttribute(new Float32Array(CAP), 1);
    data.setUsage(THREE.DynamicDrawUsage);
    tint.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < CAP; i++) {
      slot.array[i] = i;
      data.array[i * 4 + 1] = 0.001;   // vida zero = invisivel enquanto nao usado
    }
    geo.setAttribute('iData', data);
    geo.setAttribute('iTint', tint);
    geo.setAttribute('iSlot', slot);

    this._attrData = data;
    this._attrTint = tint;
    this.mesh = mesh;
    this.ctx.scene.add(mesh);
  }

  /**
   * Marca a superficie. Recebe escalares para nao alocar por tiro.
   * @returns {boolean} false se a superficie nao aceita decalque.
   */
  add(px, py, pz, nx, ny, nz, surface, sizeMul) {
    if (!this.mesh) return false;
    const st = STYLES[surface];
    if (!st) return false;

    const limit = Math.max(8, Math.min(CAP, this.ctx.settings.maxDecals ?? CAP));
    if (this._head >= limit) this._head = 0;
    const idx = this._head;
    this._head = (this._head + 1) % limit;

    _n.set(nx, ny, nz);
    if (_n.lengthSq() < 1e-8) _n.set(0, 1, 0);
    _n.normalize();

    // base ortonormal com giro aleatorio em torno da normal: dois furos vizinhos
    // com a mesma textura na mesma rotacao denunciam o decalque na hora
    if (Math.abs(_n.y) < 0.92) _t.set(-_n.z, 0, _n.x);
    else _t.set(1, 0, 0);
    _t.normalize();
    _b.crossVectors(_n, _t);

    const roll = Math.random() * Math.PI * 2;
    const cr = Math.cos(roll), sr = Math.sin(roll);
    const tx = _t.x * cr + _b.x * sr, ty = _t.y * cr + _b.y * sr, tz = _t.z * cr + _b.z * sr;
    const bx = _b.x * cr - _t.x * sr, by = _b.y * cr - _t.y * sr, bz = _b.z * cr - _t.z * sr;
    _t.set(tx, ty, tz);
    _b.set(bx, by, bz);

    _basis.makeBasis(_t, _b, _n);
    _quat.setFromRotationMatrix(_basis);

    const off = NORMAL_OFFSET + Math.random() * 0.003;
    _pos.set(px + _n.x * off, py + _n.y * off, pz + _n.z * off);

    const size = (st.s0 + Math.random() * (st.s1 - st.s0)) * (sizeMul || 1);
    // leve anisotropia: furo perfeitamente redondo tambem denuncia
    _scale.set(size * (0.92 + Math.random() * 0.16), size * (0.92 + Math.random() * 0.16), 1);

    _mat.compose(_pos, _quat, _scale);
    this.mesh.setMatrixAt(idx, _mat);

    const d = this._attrData.array;
    const t = this._attrTint.array;
    const o = idx * 4;
    d[o] = this._time;
    d[o + 1] = LIFE * (0.85 + Math.random() * 0.3);
    d[o + 2] = st.tile;
    d[o + 3] = st.rough;
    const shade = 0.86 + Math.random() * 0.28;
    t[o] = st.r * shade;
    t[o + 1] = st.g * shade;
    t[o + 2] = st.b * shade;
    t[o + 3] = st.metal;

    this._filled = Math.min(limit, Math.max(this._filled, idx + 1));
    this.mesh.count = this._filled;
    this.mesh.instanceMatrix.needsUpdate = true;
    this._attrData.needsUpdate = true;
    this._attrTint.needsUpdate = true;
    this._uLimit.value = limit;
    this._uHead.value = this._head;
    return true;
  }

  update(dt, time) {
    this._uTime.value = time;
    // mesmo relogio adiantado do resto do FX: o furo aparece cheio no primeiro
    // frame desenhado depois do tiro, sem meio frame de atraso
    this._time = time + dt;
    // a qualidade pode mudar no menu: o teto vivo acompanha sem reconstruir nada
    const limit = Math.max(8, Math.min(CAP, this.ctx.settings.maxDecals ?? CAP));
    if (this._uLimit.value !== limit) {
      this._uLimit.value = limit;
      if (this._head >= limit) this._head = 0;
      if (this._filled > limit) {
        this._filled = limit;
        if (this.mesh) this.mesh.count = limit;
      }
      this._uHead.value = this._head;
    }
  }

  clear() {
    if (!this.mesh) return;
    const d = this._attrData.array;
    for (let i = 0; i < CAP; i++) {
      d[i * 4] = 0;
      d[i * 4 + 1] = 0.001;
    }
    this._attrData.needsUpdate = true;
    this._head = 0;
    this._filled = 0;
    this.mesh.count = 0;
  }

  // -------------------------------------------------------------------------
  // Texturas procedurais
  // -------------------------------------------------------------------------

  /**
   * Atlas 2x2. Albedo em 512 (256 por tile) e normal em 256 (128 por tile).
   * O normal map sai de um campo de altura: cratera analitica + sulco nas
   * rachaduras lido do proprio albedo.
   */
  _buildTextures() {
    const S = 512, T = 256;
    const cv = document.createElement('canvas');
    cv.width = S; cv.height = S;
    const c2 = cv.getContext('2d');
    const img = c2.createImageData(S, S);
    const px = img.data;

    // angulos e comprimentos das rachaduras, fixos por tile (deterministicos)
    const crackA = [];
    const crackL = [];
    for (let tile = 0; tile < 4; tile++) {
      const n = tile === 3 ? 13 : 8;
      const as = new Float32Array(n);
      const ls = new Float32Array(n);
      for (let k = 0; k < n; k++) {
        as[k] = (k / n) * Math.PI * 2 + (hash2(k * 3.1 + tile * 7.7, tile * 2.3) - 0.5) * 0.55;
        ls[k] = tile === 3
          ? 0.55 + hash2(k * 5.3, tile + 1.7) * 0.42
          : 0.28 + hash2(k * 5.3, tile + 1.7) * 0.36;
      }
      crackA.push(as);
      crackL.push(ls);
    }

    // O desvio da rachadura depende so do raio (o segundo eixo do ruido e fixo
    // por rachadura). Tabelar isso troca ~2 milhoes de chamadas de ruido por
    // uma interpolacao linear - o custo do init cai a menos da metade e a
    // imagem sai igual.
    const JB = 192;              // amostras de raio, de 0 a 1.5
    const JS = JB / 1.5;
    const jitTab = [];
    for (let tile = 0; tile < 4; tile++) {
      const n = crackA[tile].length;
      const tab = new Float32Array(n * (JB + 1));
      for (let k = 0; k < n; k++) {
        const fx = tile === 3 ? 5.5 : 7;
        const kx = tile === 3 ? k * 9.1 : k * 13.7;
        const ky = tile === 3 ? k * 4.7 : k * 3.3;
        const amp = tile === 3 ? 0.30 : 0.42;
        for (let j = 0; j <= JB; j++) {
          tab[k * (JB + 1) + j] = (vnoise((j / JS) * fx + kx, ky) - 0.5) * amp;
        }
      }
      jitTab.push(tab);
    }
    const jitter = (tile, k, r) => {
      const tab = jitTab[tile];
      let f = r * JS;
      if (f < 0) f = 0; else if (f > JB - 1) f = JB - 1;
      const i = f | 0;
      const g = f - i;
      const b = k * (JB + 1) + i;
      return tab[b] + (tab[b + 1] - tab[b]) * g;
    };

    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const tx = x >= T ? 1 : 0;
        const ty = y >= T ? 1 : 0;
        const tile = ty * 2 + tx;
        const u = (x - tx * T) / T;
        const v = (y - ty * T) / T;
        const cx = (u - 0.5) * 2;
        const cy = (v - 0.5) * 2;
        const r = Math.sqrt(cx * cx + cy * cy);
        const ang = Math.atan2(cy, cx);

        let a = 0, lum = 0.5;

        if (tile === 0 || tile === 2) {
          const metal = tile === 2;
          const wob = 1 + (vnoise(Math.cos(ang) * 3.1 + 5, Math.sin(ang) * 3.1 + 2) - 0.5) * 0.45;
          const rh = (metal ? 0.135 : 0.185) * wob;
          const ringOut = metal ? 0.30 : 0.66;

          // po/estilhacamento em volta do furo
          const grain = fbm(u * 9 + tile * 3, v * 9 + 1, 4);
          let ring = (1 - smooth(rh * 0.9, ringOut, r)) * (0.30 + 0.72 * grain);
          if (metal) ring *= 0.55;
          a = ring;
          lum = metal ? 0.92 : 0.80 + 0.25 * grain;

          // rachaduras / riscos radiais
          const as = crackA[tile], ls = crackL[tile];
          let crack = 0;
          for (let k = 0; k < as.length; k++) {
            const da = Math.abs(wrapAngle(ang - as[k] - jitter(tile, k, r)));
            const len = ls[k] * (metal ? 0.75 : 1);
            if (r > len) continue;
            const w = 0.012 + r * 0.05;
            const prof = Math.exp(-((da * r) * (da * r)) / (w * w));
            crack = Math.max(crack, prof * (1 - r / len));
          }
          a = Math.min(1, a + crack * 0.85);
          if (crack > 0.15) lum = metal ? 0.95 : 0.88;

          // o furo em si
          const hole = 1 - smooth(rh * 0.72, rh, r);
          if (hole > 0) {
            a = Math.min(1, a + hole);
            lum = lum * (1 - hole) + (metal ? 0.045 : 0.055) * hole;
          }
          if (metal) {
            // aro de metal exposto, bem mais claro que a chapa em volta
            const rim = Math.exp(-Math.pow((r - rh * 1.35) / (rh * 0.5), 2));
            a = Math.min(1, a + rim * 0.9);
            lum = lum * (1 - rim * 0.8) + 1.0 * rim * 0.8;
          }
        } else if (tile === 1) {
          // Cratera em material macio. Sem anel simetrico de proposito: aro
          // regular vira rosquinha, que e a cara de decalque desenhado a mao.
          // O que existe de verdade e material deslocado, irregular e manchado.
          const wob = 1 + (vnoise(Math.cos(ang) * 2.2 + 3, Math.sin(ang) * 2.2 + 9) - 0.5) * 0.55;
          const grain = fbm(u * 8 + 13, v * 8 + 6, 4);
          const rOut = 0.62 * wob;
          a = (1 - smooth(rOut * 0.30, rOut, r)) * (0.30 + 0.95 * grain);

          // ejecta: rastros curtos de terra jogada para fora
          const as = crackA[1], ls = crackL[1];
          let ray = 0;
          for (let k = 0; k < as.length; k++) {
            const len = ls[k] * 2.0;
            if (r > len) continue;
            const da = Math.abs(wrapAngle(ang - as[k] - jitter(1, k, r) * 1.6));
            const w = 0.05 + r * 0.13;
            ray = Math.max(ray, Math.exp(-((da * r) * (da * r)) / (w * w)) * (1 - r / len));
          }
          a = Math.min(1, a + ray * grain * 0.7);

          const deep = 1 - smooth(0.04, 0.26 * wob, r);
          lum = (0.56 + 0.34 * grain) * (1 - deep) + 0.09 * deep;
        } else {
          // vidro: teia de rachaduras claras, quase tudo transparente
          const as = crackA[3], ls = crackL[3];
          let crack = 0;
          for (let k = 0; k < as.length; k++) {
            if (r > ls[k]) continue;
            const da = Math.abs(wrapAngle(ang - as[k] - jitter(3, k, r)));
            const w = 0.010 + r * 0.030;
            const prof = Math.exp(-((da * r) * (da * r)) / (w * w));
            crack = Math.max(crack, prof * (1 - Math.pow(r / ls[k], 2.2)));
          }
          // aneis concentricos irregulares ligando as rachaduras radiais
          for (let ring = 0; ring < 3; ring++) {
            const rr = 0.24 + ring * 0.21;
            const wob = 1 + (vnoise(Math.cos(ang) * 4 + ring * 11, Math.sin(ang) * 4) - 0.5) * 0.34;
            const dr = Math.abs(r - rr * wob);
            const prof = Math.exp(-(dr * dr) / (0.0075 * 0.0075)) * (1 - ring * 0.22);
            crack = Math.max(crack, prof * 0.85);
          }
          const core = 1 - smooth(0.035, 0.10, r);
          a = Math.min(1, crack * 0.95 + core);
          lum = 0.90 + 0.18 * crack;
          if (core > 0) lum = lum * (1 - core) + 0.72 * core;
          if (r > 0.94) a = 0;
        }

        a = clamp01(a);
        lum = Math.min(1, Math.max(0, lum));
        const c = Math.round(lum * 255);
        const o = (y * S + x) * 4;
        px[o] = c; px[o + 1] = c; px[o + 2] = c;
        px[o + 3] = Math.round(a * 255);
      }
    }
    c2.putImageData(img, 0, 0);

    // --- normal map a partir de um campo de altura --------------------------
    const NS = 256, NT = 128;
    const height = new Float32Array(NS * NS);
    for (let y = 0; y < NS; y++) {
      for (let x = 0; x < NS; x++) {
        const tx = x >= NT ? 1 : 0;
        const ty = y >= NT ? 1 : 0;
        const tile = ty * 2 + tx;
        const u = (x - tx * NT) / NT;
        const v = (y - ty * NT) / NT;
        const cx = (u - 0.5) * 2;
        const cy = (v - 0.5) * 2;
        const r = Math.sqrt(cx * cx + cy * cy);

        let h = 0;
        if (tile === 0) {
          h = -Math.exp(-Math.pow(r / 0.19, 2)) * 1.0 + Math.exp(-Math.pow((r - 0.28) / 0.13, 2)) * 0.30;
        } else if (tile === 1) {
          h = -Math.exp(-Math.pow(r / 0.30, 2)) * 0.75 + Math.exp(-Math.pow((r - 0.42) / 0.18, 2)) * 0.22;
        } else if (tile === 2) {
          h = -Math.exp(-Math.pow(r / 0.14, 2)) * 1.25 + Math.exp(-Math.pow((r - 0.20) / 0.09, 2)) * 0.55;
        } else {
          h = -Math.exp(-Math.pow(r / 0.09, 2)) * 0.5;
        }
        // sulco nas rachaduras: reaproveita o alpha do albedo (2x a resolucao)
        const ao = ((y * 2) * S + (x * 2)) * 4 + 3;
        const cover = px[ao] / 255;
        h -= cover * (tile === 3 ? 0.55 : 0.16);
        h += (vnoise(u * 26 + tile * 5, v * 26) - 0.5) * 0.05;
        height[y * NS + x] = h;
      }
    }

    const ncv = document.createElement('canvas');
    ncv.width = NS; ncv.height = NS;
    const nc2 = ncv.getContext('2d');
    const nimg = nc2.createImageData(NS, NS);
    const npx = nimg.data;
    const STRENGTH = 6.0;
    for (let y = 0; y < NS; y++) {
      for (let x = 0; x < NS; x++) {
        const tx = x >= NT ? NT : 0;
        const ty = y >= NT ? NT : 0;
        // derivadas presas dentro do tile, para nao vazar de um tile ao vizinho
        const xm = Math.max(tx, x - 1), xp = Math.min(tx + NT - 1, x + 1);
        const ym = Math.max(ty, y - 1), yp = Math.min(ty + NT - 1, y + 1);
        const dhx = (height[y * NS + xp] - height[y * NS + xm]) * STRENGTH;
        const dhy = (height[yp * NS + x] - height[ym * NS + x]) * STRENGTH;
        let nx = -dhx, ny = -dhy, nz = 1;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
        nx /= len; ny /= len; nz /= len;
        const o = (y * NS + x) * 4;
        npx[o] = Math.round((nx * 0.5 + 0.5) * 255);
        npx[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
        npx[o + 2] = Math.round((nz * 0.5 + 0.5) * 255);
        npx[o + 3] = 255;
      }
    }
    nc2.putImageData(nimg, 0, 0);

    const map = new THREE.CanvasTexture(cv);
    // flipY FALSE nos dois: o tile e escolhido em espaco de pixel do canvas, e o
    // normal map precisa ficar alinhado ao albedo (o campo de altura sai dele)
    map.flipY = false;
    map.colorSpace = THREE.SRGBColorSpace;
    map.generateMipmaps = true;
    map.minFilter = THREE.LinearMipmapLinearFilter;
    map.magFilter = THREE.LinearFilter;
    map.wrapS = THREE.ClampToEdgeWrapping;
    map.wrapT = THREE.ClampToEdgeWrapping;
    map.anisotropy = Math.min(8, this.ctx.settings.anisotropy ?? 4);
    map.needsUpdate = true;

    const normal = new THREE.CanvasTexture(ncv);
    normal.flipY = false;
    normal.colorSpace = THREE.NoColorSpace;
    normal.generateMipmaps = true;
    normal.minFilter = THREE.LinearMipmapLinearFilter;
    normal.magFilter = THREE.LinearFilter;
    normal.wrapS = THREE.ClampToEdgeWrapping;
    normal.wrapT = THREE.ClampToEdgeWrapping;
    normal.anisotropy = Math.min(4, this.ctx.settings.anisotropy ?? 4);
    normal.needsUpdate = true;

    return { map, normal };
  }

  dispose() {
    if (this.mesh) {
      this.mesh.parent?.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.material?.dispose();
    this.mapTex?.dispose();
    this.normalTex?.dispose();
  }
}

function clamp01(v) {
  return v < 0 ? 0 : (v > 1 ? 1 : v);
}

function smooth(a, b, x) {
  if (b === a) return x < a ? 0 : 1;
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

function noopRaycast() { /* decalque nunca entra em raycast de gameplay */ }
